import { useQuery } from '@tanstack/react-query'
import { v2GetOutreachPricingOptions } from '@workspace/api-backend/react-query'
import { Button } from '@workspace/ui/components/button'
import { Checkbox } from '@workspace/ui/components/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@workspace/ui/components/dialog'
import { TextInput } from '@workspace/ui/components/text-input'
import { cn } from '@workspace/ui/lib/utils'
import { useId, useState } from 'react'
import { useWalletTopUpMutation } from '../../../hooks/api'
import { formatUsdCents } from '../../../lib/format'
import { trackEvent } from '../../../lib/gtag'
import { TOPUP_REFUND_LINE, WALLET_SPEND_CAP_LINE } from '../config/launch-checklist'

const emphasis = 'font-semibold text-foreground'

interface Props {
  startupId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  title?: string
  description?: string
  submitLabel?: string
  /** Where Stripe returns the user after checkout. Defaults to the outreach page. */
  returnTo?: 'outreach' | 'billing'
  /** Whether to activate the outreach campaign on a successful top-up. */
  activateOutreach?: boolean
}

export function OutreachTopUpDialog({
  startupId,
  open,
  onOpenChange,
  title = 'Top up your wallet',
  description = "Pick an amount. You'll be redirected to Stripe to enter your card.",
  submitLabel = 'Continue to Stripe',
  returnTo = 'outreach',
  activateOutreach = false,
}: Props) {
  const pricingQuery = useQuery(v2GetOutreachPricingOptions())
  const pricing = pricingQuery.data?.status === 'ok' ? pricingQuery.data.data : null
  // Fallback mirrors OUTREACH_DEFAULT_DEPOSIT_OPTIONS_CENTS on the backend.
  const options = pricing?.defaultDepositOptionsCents ?? [5000, 10000, 25000]
  const minDeposit = pricing?.minDepositCents ?? 5000

  const [selectedCents, setSelectedCents] = useState<number | 'custom'>(options[0] ?? 5000)
  const [customCents, setCustomCents] = useState<string>('')
  const [agreedSending, setAgreedSending] = useState(false)
  const [agreedLimits, setAgreedLimits] = useState(false)
  const customId = useId()
  const sendingId = useId()
  const limitsId = useId()

  const checkout = useWalletTopUpMutation(startupId)

  const amountCents =
    selectedCents === 'custom' ? Math.round(Number(customCents) * 100) || 0 : selectedCents
  const validAmount = amountCents >= minDeposit
  const errorText =
    selectedCents === 'custom' && customCents && !validAmount
      ? `Minimum deposit is ${formatUsdCents(minDeposit)}`
      : null

  // LinkedIn-automation consent is only required when this top-up also activates
  // the outreach campaign. A plain wallet top-up (e.g. from billing) skips it.
  const consentSatisfied = !activateOutreach || (agreedSending && agreedLimits)
  const canSubmit = validAmount && consentSatisfied

  function handleOpenChange(next: boolean) {
    // Reset consent each time the dialog closes so it must be re-confirmed.
    if (!next) {
      setAgreedSending(false)
      setAgreedLimits(false)
    }
    onOpenChange(next)
  }

  function handleSubmit() {
    if (!canSubmit) return
    checkout.mutate(
      { path: { id: startupId }, body: { amountCents, returnTo, activateOutreach } },
      {
        onSuccess: (response) => {
          if (response.status === 'ok' && response.data.checkoutUrl) {
            trackEvent('outreach_topup_checkout_started', {
              startup_id: startupId,
              value: amountCents / 100,
              currency: 'USD',
            })
            window.location.assign(response.data.checkoutUrl)
          }
        },
      }
    )
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-2">
            {options.map((cents) => (
              <button
                key={cents}
                type="button"
                onClick={() => setSelectedCents(cents)}
                className={cn(
                  'rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors',
                  selectedCents === cents
                    ? 'border-primary bg-primary/10 text-foreground'
                    : 'border-border/60 bg-background/40 text-muted-foreground hover:text-foreground'
                )}
              >
                {formatUsdCents(cents)}
              </button>
            ))}
          </div>

          <button
            type="button"
            onClick={() => setSelectedCents('custom')}
            className={cn(
              'w-full rounded-lg border px-3 py-2 text-left text-xs transition-colors',
              selectedCents === 'custom'
                ? 'border-primary bg-primary/10 text-foreground'
                : 'border-border/60 bg-background/40 text-muted-foreground hover:text-foreground'
            )}
          >
            Custom amount
          </button>

          {selectedCents === 'custom' && (
            <div>
              <label
                htmlFor={customId}
                className="text-[11px] uppercase tracking-wider text-muted-foreground"
              >
                Amount in USD
              </label>
              <div className="mt-1.5 flex items-center gap-2">
                <span className="text-sm text-muted-foreground">$</span>
                <TextInput
                  id={customId}
                  type="number"
                  inputSize="sm"
                  min={minDeposit / 100}
                  step={50}
                  value={customCents}
                  onChange={(e) => setCustomCents(e.target.value)}
                  className="max-w-[160px]"
                  placeholder="100"
                />
              </div>
              {errorText && <p className="mt-1 text-[11px] text-amber-400">{errorText}</p>}
            </div>
          )}

          <div className="space-y-1.5 rounded-lg border border-border/40 bg-background/40 px-3 py-2.5">
            <p className="text-[12.5px] font-medium leading-relaxed text-foreground">
              {WALLET_SPEND_CAP_LINE}
            </p>
            <p className="text-[11.5px] leading-relaxed text-muted-foreground">
              {TOPUP_REFUND_LINE}
            </p>
          </div>
        </div>

        {activateOutreach && (
          <div className="space-y-3 border-t border-border/40 pt-4">
            <p className="text-[13px] font-medium text-foreground">
              By continuing, you agree that:
            </p>
            <label htmlFor={sendingId} className="flex cursor-pointer items-start gap-2.5">
              <Checkbox
                id={sendingId}
                checked={agreedSending}
                onCheckedChange={(value) => setAgreedSending(value === true)}
                className="mt-0.5"
              />
              <span className="text-[12.5px] leading-relaxed text-muted-foreground">
                SeedForge may send up to <span className={emphasis}>20 connection requests</span>,{' '}
                <span className={emphasis}>20 follow-up messages</span>,{' '}
                <span className={emphasis}>20 profile views</span>, and{' '}
                <span className={emphasis}>20 post likes</span> per day from your LinkedIn account.
              </span>
            </label>
            <label htmlFor={limitsId} className="flex cursor-pointer items-start gap-2.5">
              <Checkbox
                id={limitsId}
                checked={agreedLimits}
                onCheckedChange={(value) => setAgreedLimits(value === true)}
                className="mt-0.5"
              />
              <span className="text-[12.5px] leading-relaxed text-muted-foreground">
                You will not exceed <span className={emphasis}>20 connection requests</span> and{' '}
                <span className={emphasis}>20 follow-up messages</span> per day from your own
                activity, to keep your account within LinkedIn's daily limits.
              </span>
            </label>
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={checkout.isPending}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit || checkout.isPending}>
            {checkout.isPending ? 'Loading…' : submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
